import React from "react";

import { Col } from "antd";

import FlexContainer from "shared/components/FlexContainer";
import Spacer from "shared/components/Spacer";
import {
  COLORS,
  PERSONA_VERIFICATION_STATUS,
  PERSONA_VERIFICATION_TYPES_LABELS,
  VERIFICATION_OF_RESIDENCY_STATUS,
  PERSONA_VERIFICATION_STATUS_TYPES,
  VERIFICATION_OF_RESIDENCY_STATUS_TYPES,
  VERIFICATION_TYPES,
} from "shared/config/constants";
import { isProductionEnv } from "shared/config/constants/env.constants";
import { formatDateTime, getValueOrNA } from "shared/utils/ui";

import Icon from "../Icon";
import { RegularText } from "../Typography";

import {
  StatusWrapper,
  StatusLeftContainer,
  StatusLabel,
  InlineStatusRow,
} from "./styled";

const VERIFICATION_STATUS_VIEW_INFO = Object.freeze({
  [PERSONA_VERIFICATION_STATUS.COMPLETED]: {
    label: "Verified",
    subHeaderLabel: "verification attempt completed",
    Icon: Icon.CheckGreenBig,
    color: COLORS.accentDark,
  },
  [PERSONA_VERIFICATION_STATUS.PASSED]: {
    label: "Verified",
    subHeaderLabel: "verification attempt completed",
    Icon: Icon.CheckGreenBig,
    color: COLORS.accentDark,
  },
  [PERSONA_VERIFICATION_STATUS.APPROVED]: {
    label: "Verified",
    subHeaderLabel: "verification attempt completed",
    Icon: Icon.CheckGreenBig,
    color: COLORS.accentDark,
  },
  [PERSONA_VERIFICATION_STATUS.SKIPPED]: {
    label: "Skipped",
    subHeaderLabel: "verification attempt skipped",
    Icon: Icon.OrangeExclamation,
    color: COLORS.midOrange,
  },
  [PERSONA_VERIFICATION_STATUS.FAILED]: {
    label: "Verification Failed",
    subHeaderLabel: "verification attempt failed",
    Icon: Icon.CrossRedBig,
    color: COLORS.failedRed,
  },
  [PERSONA_VERIFICATION_STATUS.DECLINED]: {
    label: "Unverified",
    subHeaderLabel: "verification attempt failed",
    Icon: Icon.CrossRedBig,
    color: COLORS.failedRed,
  },
  [PERSONA_VERIFICATION_STATUS.EXPIRED]: {
    label: "Expired",
    subHeaderLabel: "Expired",
    Icon: Icon.OrangeExclamation,
    color: COLORS.midOrange,
  },
  pending: {
    label: "Pending",
    subHeaderLabel: "verification pending",
    Icon: null,
    color: COLORS.textColorPrimary,
  },
  [VERIFICATION_OF_RESIDENCY_STATUS.SUBMITTED]: {
    label: "Submitted",
    subHeaderLabel: "Submitted",
    Icon: Icon.CheckGreenBig,
    color: COLORS.accentDark,
  },
  [VERIFICATION_OF_RESIDENCY_STATUS.WAITING]: {
    label: "Not submitted",
    subHeaderLabel: "Not submitted",
    Icon: null,
    color: COLORS.failedRed,
  },
  [VERIFICATION_OF_RESIDENCY_STATUS.STARTED]: {
    label: "Not submitted",
    subHeaderLabel: "Not submitted",
    Icon: null,
    color: COLORS.failedRed,
  },
  [VERIFICATION_OF_RESIDENCY_STATUS.NOTIFIED]: {
    label: "Notified",
    subHeaderLabel: "Notified",
    Icon: null,
    color: COLORS.midOrange,
  },
  [VERIFICATION_OF_RESIDENCY_STATUS.VERIFIED]: {
    label: "Notified",
    subHeaderLabel: "Notified",
    Icon: null,
    color: COLORS.midOrange,
  },
});

const VERIFICATION_TYPE_VIEW_INFO = Object.freeze({
  [VERIFICATION_TYPES.ID]: "ID",
  [VERIFICATION_TYPES.VOR]: "Residency",
});

/**
 * Component that displays the verification status details.
 */

export interface IVerificationStatus {
  type: VERIFICATION_TYPES;
  verificationType?: string;
  status:
    | PERSONA_VERIFICATION_STATUS_TYPES
    | VERIFICATION_OF_RESIDENCY_STATUS_TYPES
    | "pending";
  verificationDate?: string;
  subHeader?: boolean;
}

const AccordionVerificationStatus = ({
  type,
  verificationType = undefined,
  status,
  verificationDate = undefined,
  subHeader = false,
}: IVerificationStatus) => {
  const statusInfo = VERIFICATION_STATUS_VIEW_INFO[status];

  // Determine display type with improved fallback logic
  let displayType;
  if (verificationType) {
    displayType = PERSONA_VERIFICATION_TYPES_LABELS[verificationType];

    // Dev warning for unmapped verification types in non-production environments
    if (!displayType && !isProductionEnv) {
      console.warn(
        `Unmapped verificationType encountered: "${verificationType}". Please add it to PERSONA_VERIFICATION_TYPES_LABELS.`
      );
    }

    // Fallback to "Verification" if no label exists
    displayType = displayType || "Verification";
  } else {
    displayType = VERIFICATION_TYPE_VIEW_INFO[type];
  }

  const renderSubHeader = () => {
    return (
      <InlineStatusRow type="flex" align="middle">
        <Col xs={24} md={24} lg={24}>
          <FlexContainer justifyContent="space-between">
            <FlexContainer alignItems="center">
              {statusInfo?.Icon && (
                <statusInfo.Icon
                  width="14.5"
                  height="14.5"
                  className="status-icon"
                />
              )}
              <RegularText>
                {getValueOrNA(`${displayType} ${statusInfo?.subHeaderLabel}`)}
              </RegularText>
            </FlexContainer>
            <FlexContainer>
              {verificationDate && (
                <RegularText>{formatDateTime(verificationDate)}</RegularText>
              )}
            </FlexContainer>
          </FlexContainer>
        </Col>
        <Spacer hideGteMd />
      </InlineStatusRow>
    );
  };

  return (
    <>
      {subHeader && renderSubHeader()}
      {!subHeader && (
        <StatusWrapper>
          <StatusLeftContainer>
            {statusInfo?.Icon && (
              <statusInfo.Icon width="24" height="24" className="status-icon" />
            )}
            <StatusLabel color={statusInfo?.color} fontWeight={600}>
              {getValueOrNA(`${displayType} ${statusInfo?.label}`)}
            </StatusLabel>
          </StatusLeftContainer>
        </StatusWrapper>
      )}
    </>
  );
};

export default AccordionVerificationStatus;
